import { supabase } from '@/integrations/supabase/client';
import { extractEdgeFunctionError } from '@/lib/edgeFunctionError';

/**
 * Verificación facial decidida por el SERVIDOR: la Edge Function genérica
 * `verificar-rostro` (repo sozu-edge-functions), la misma que usan las apps de
 * agentes y clientes. El navegador solo manda la selfie; la comparación con la
 * identificación, la validación del expediente y su historial los hace la function.
 *
 * Antes el navegador comparaba con `human-face` y escribía él mismo
 * `id_estatus_verificacion = 2`: desde la consola cualquiera validaba su identidad.
 */
export type EstadoRostro = 'verificada' | 'en_revision' | 'reintentar';

export type Aviso = {
  tipo: 'exito' | 'info' | 'error';
  texto: string;
  /** El intento terminó (verificada o en revisión): se cierra el diálogo. */
  cerrar: boolean;
};

const ERRORES: Record<string, string> = {
  demasiados_intentos: 'Llegaste al límite de intentos de hoy. Vuelve a intentarlo mañana.',
  sin_identificacion: 'Primero agrega tu INE o pasaporte para verificar tu identidad.',
  persona_distinta: 'La verificación facial la hace el titular desde su propia sesión.',
  impersonation_not_allowed: 'La verificación facial la hace el titular, no se puede hacer viendo como él.',
  no_desde_delegacion: 'La verificación facial la hace el titular, no un acceso delegado.',
  archivo_demasiado_grande: 'La foto es demasiado grande. Intenta de nuevo.',
};

/** Traduce la respuesta (o el código de error) de la function a lo que ve el usuario. */
export function avisoDeVerificacion(
  r: { estado: EstadoRostro; motivo: string | null } | { error: string },
): Aviso {
  if ('error' in r) {
    return { tipo: 'error', texto: ERRORES[r.error] ?? 'No se pudo verificar tu identidad. Intenta de nuevo.', cerrar: false };
  }
  if (r.estado === 'verificada') return { tipo: 'exito', texto: 'Identidad verificada correctamente.', cerrar: true };
  if (r.estado === 'en_revision') {
    return { tipo: 'info', texto: r.motivo ?? 'El equipo revisará tu verificación y te avisaremos.', cerrar: true };
  }
  return { tipo: 'error', texto: r.motivo ?? 'Tómate otra foto de frente y con buena luz.', cerrar: false };
}

function aBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(String(lector.result).split(',')[1] ?? '');
    lector.onerror = () => reject(lector.error);
    lector.readAsDataURL(blob);
  });
}

/**
 * Manda la selfie. `idPersonaEsperada` es la persona cuyo expediente se muestra:
 * si no es la de la sesión, el servidor responde `persona_distinta` y no toca nada.
 */
export async function verificarRostroEnServidor(
  selfie: Blob,
  idPersonaEsperada: number | undefined,
): Promise<{ aviso: Aviso; estado: EstadoRostro | null }> {
  const base64 = await aBase64(selfie);
  const { data, error } = await supabase.functions.invoke('verificar-rostro', {
    body: {
      base64,
      content_type: selfie.type || 'image/jpeg',
      ...(idPersonaEsperada ? { id_persona_esperada: idPersonaEsperada } : {}),
    },
  });
  if (error) {
    const codigo = await extractEdgeFunctionError(error);
    return { aviso: avisoDeVerificacion({ error: codigo }), estado: null };
  }
  const estado = (data?.estado ?? 'reintentar') as EstadoRostro;
  return { aviso: avisoDeVerificacion({ estado, motivo: data?.motivo ?? null }), estado };
}
